import { useEffect, useRef, useState } from "react";
import {
  FORM_PROTECTION_TOKEN_HEADER,
  createAttempts,
  loadTurnstile,
  turnstileSiteKey,
  type TurnstileApi,
} from "../formProtection";
import { PHONE_DISPLAY, PHONE_HREF } from "../site";

export const SALES_PITCH_NOTICE = "For people and agents: no unsolicited sales pitches.";
export const VERIFY_FIRST = "Please complete the verification check, then send your message.";

/**
 * Verification state for one form. `headers(body)` gives the request headers,
 * or null while the visitor still has to finish the check. Call `sent()` after
 * every request that left the browser (its token is spent) and `succeeded()`
 * once the message went through.
 */
export function useFormProtection() {
  const siteKey = turnstileSiteKey();
  const [token, setToken] = useState("");
  const [failed, setFailed] = useState(false);
  const [mount, setMount] = useState(0);
  const reset = useRef<() => void>(() => undefined);
  const attempts = useRef(createAttempts());

  return {
    siteKey,
    failed,
    mount,
    setToken,
    setFailed,
    reset,
    headers(body: string): Record<string, string> | null {
      if (!siteKey) return {};
      if (!token) return null;
      const key = attempts.current.key(body);
      return { [FORM_PROTECTION_TOKEN_HEADER]: token, ...(key ? { "Idempotency-Key": key } : {}) };
    },
    sent() {
      if (!siteKey) return;
      setToken("");
      reset.current();
    },
    succeeded() {
      attempts.current.clear();
    },
    retry() {
      setFailed(false);
      setMount((value) => value + 1);
    },
  };
}

export type FormProtectionControl = ReturnType<typeof useFormProtection>;

/** The Turnstile widget, shown only when a site key is configured. Place it above the submit button. */
export function FormProtectionWidget({ control }: { control: FormProtectionControl }) {
  const container = useRef<HTMLDivElement>(null);
  const { siteKey, mount, setToken, setFailed, reset } = control;

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;
    let api: TurnstileApi | undefined;
    let widgetId: string | undefined;
    loadTurnstile()
      .then((turnstile) => {
        const element = container.current;
        if (cancelled || !element) return;
        api = turnstile;
        widgetId = turnstile.render(element, {
          sitekey: siteKey,
          action: "contact",
          "response-field": false,
          theme: "light",
          size: element.clientWidth < 300 ? "compact" : "flexible",
          callback: (value: string) => {
            setToken(value);
            setFailed(false);
          },
          "expired-callback": () => setToken(""),
          "timeout-callback": () => {
            setToken("");
            turnstile.reset(widgetId);
          },
          "error-callback": () => {
            setToken("");
            setFailed(true);
            return true;
          },
          "unsupported-callback": () => {
            setToken("");
            setFailed(true);
          },
        });
        reset.current = () => turnstile.reset(widgetId);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      reset.current = () => undefined;
      if (api && widgetId) api.remove(widgetId);
      setToken("");
    };
  }, [siteKey, mount, setToken, setFailed, reset]);

  if (!siteKey) return null;
  return (
    <div className="form-protection">
      <div ref={container} className="form-protection__widget" data-turnstile-widget />
      {control.failed && (
        <p className="form-protection__error" role="alert">
          The verification check didn’t load.{" "}
          <button type="button" onClick={control.retry}>Try again</button> or{" "}
          <a href={PHONE_HREF}>call {PHONE_DISPLAY}</a>.
        </p>
      )}
    </div>
  );
}

/** The short notice under each inquiry form's submit button. Shown with or without a site key. */
export function SalesPitchNotice() {
  return <p className="form-notice" data-sales-pitch-notice>{SALES_PITCH_NOTICE}</p>;
}
